import { ReactNode } from "react";
import "./code-editor-shell.scss";

export type ECodeEditorDrawerTab = "result" | "messages";

/**
 * The bottom drawer: what the last run returned, the parameters it ran with, and whatever
 * the language server or the database had to say. One place instead of three.
 *
 * Closed, it is just its own tab strip along the bottom edge — a console's handle. A run
 * grows it, and so does clicking a tab or the chevron.
 */
export interface ICodeEditorDrawerProps {
  /** Owned by the host so the splitter can drag it. */
  height: number;
  /** Closed to the tab strip. The host owns it so a run can open the console. */
  isCollapsed: boolean;
  onToggleCollapsed: (isCollapsed: boolean) => void;
  activeTab: ECodeEditorDrawerTab;
  onTabChange: (tab: ECodeEditorDrawerTab) => void;
  /** Rows the last run returned; null when it has not run yet. */
  resultCount: number | null;
  messageCount: number;
  /** Right-hand summary, e.g. "first 5 of 24 rows · 38 ms". */
  meta?: string;
  onExport?: () => void;
  resultContent: ReactNode;
  messagesContent: ReactNode;
}

interface ITabDefinition {
  id: ECodeEditorDrawerTab;
  label: string;
  count: number | null;
  isGood: boolean;
  testId: string;
}

export const CodeEditorDrawer = (props: ICodeEditorDrawerProps): JSX.Element => {
  const tabs: ITabDefinition[] = [
    { id: "result", label: "Result", count: props.resultCount, isGood: true, testId: "testResultTitle" },
    { id: "messages", label: "Messages", count: props.messageCount, isGood: false, testId: "drawerMessagesTab" }
  ];

  // Collapsed, a tab both opens the console and picks what it opens on.
  const handleTabClick = (tab: ECodeEditorDrawerTab): void => {
    if (props.isCollapsed) props.onToggleCollapsed(false);
    props.onTabChange(tab);
  };

  return (
    <div
      className={`kuika__code-editor__drawer${props.isCollapsed ? " collapsed" : ""}`}
      style={props.isCollapsed ? undefined : { height: props.height }}
      data-testid="codeEditorDrawer"
    >
      <div className="kuika__code-editor__drawer__tabs">
        {tabs.map((tab: ITabDefinition) => (
          <button
            type="button"
            key={tab.id}
            className={`kuika__code-editor__drawer__tab${!props.isCollapsed && props.activeTab === tab.id ? " active" : ""}`}
            onClick={(): void => handleTabClick(tab.id)}
            data-testid={tab.testId}
          >
            {tab.label}
            {tab.count !== null && tab.count > 0 && (
              <span className={`kuika__code-editor__badge kuika__code-editor__badge--${tab.isGood ? "ok" : "warn"}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}

        <div className="kuika__code-editor__drawer__meta">
          {props.meta && <span>{props.meta}</span>}
          {props.onExport && (
            <button
              type="button"
              className="kuika__code-editor__icon-button"
              aria-label="Export result"
              onClick={props.onExport}
              data-testid="exportResultButton"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 3v12M7 10l5 5 5-5M4 20h16" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}

          <button
            type="button"
            className="kuika__code-editor__icon-button"
            aria-label={props.isCollapsed ? "Open the console" : "Close the console"}
            aria-expanded={!props.isCollapsed}
            onClick={(): void => props.onToggleCollapsed(!props.isCollapsed)}
            data-testid="toggleConsoleButton"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transform: props.isCollapsed ? "none" : "rotate(180deg)" }}
            >
              <path d="M6 14l6-6 6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {!props.isCollapsed && (
        <div className="kuika__code-editor__drawer__body">
          {props.activeTab === "result" && props.resultContent}
          {props.activeTab === "messages" && props.messagesContent}
        </div>
      )}
    </div>
  );
};
